"use client";

import Image from "next/image";
import { Close } from "@mui/icons-material";
import { Box, Dialog, IconButton } from "@mui/material";
import { useState } from "react";

type ScreenshotLightboxProps = {
  src: string;
  alt: string;
  width: number;
  height: number;
  sizes?: string;
  priority?: boolean;
  inlineMaxHeight?: number | string;
};

export function ScreenshotLightbox({
  src,
  alt,
  width,
  height,
  sizes,
  priority,
  inlineMaxHeight,
}: ScreenshotLightboxProps) {
  const [open, setOpen] = useState(false);
  const openLightbox = () => {
    setOpen(true);
    window.dispatchEvent(
      new CustomEvent("screenshot-lightbox", { detail: { open: true } }),
    );
  };
  const closeLightbox = () => {
    setOpen(false);
    window.dispatchEvent(
      new CustomEvent("screenshot-lightbox", { detail: { open: false } }),
    );
  };

  return (
    <>
      <Box
        component="button"
        type="button"
        aria-label={`Ampliar screenshot: ${alt}`}
        onClick={openLightbox}
        sx={{
          display: "block",
          width: "100%",
          p: 0,
          m: 0,
          border: 0,
          background: "transparent",
          cursor: "zoom-in",
          textAlign: "inherit",
          "&:focus-visible": {
            outline: "3px solid hsl(var(--primary))",
            outlineOffset: 2,
          },
        }}
      >
        <Image
          src={src}
          alt={alt}
          width={width}
          height={height}
          sizes={sizes}
          priority={priority}
          loading={priority ? "eager" : "lazy"}
          style={{
            width: "100%",
            height: "auto",
            maxWidth: "100%",
            maxHeight: inlineMaxHeight,
            display: "block",
            objectFit: "contain",
          }}
        />
      </Box>
      <Dialog
        open={open}
        onClose={closeLightbox}
        aria-label={`Screenshot ampliado: ${alt}`}
        fullScreen
        slotProps={{
          paper: {
            sx: {
              // The dark frame around the image must go: the paper is now a
              // transparent viewport-sized layer, with no shadow or border.
              // The scrim is delegated to the backdrop slot below.
              m: 0,
              width: "100%",
              height: "100%",
              maxWidth: "none",
              maxHeight: "none",
              backgroundColor: "transparent",
              backgroundImage: "none",
              boxShadow: "none",
              border: 0,
              borderRadius: 0,
              overflow: "auto",
              overscrollBehavior: "contain",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            },
          },
          backdrop: {
            sx: {
              // Dark scrim uses the foreground token so it adapts to the
              // active theme (light/dark) via the CSS variables defined in
              // `src/app/globals.css`.
              backgroundColor: "hsl(var(--foreground) / 0.78)",
            },
          },
        }}
        sx={{
          "@media (prefers-reduced-motion: reduce)": {
            "& .MuiDialog-paper, & .MuiBackdrop-root": {
              transition: "none",
            },
          },
        }}
      >
        <IconButton
          onClick={closeLightbox}
          aria-label="Fechar screenshot ampliado"
          sx={{
            position: "fixed",
            top: { xs: 8, md: 20 },
            right: { xs: 8, md: 20 },
            zIndex: 1,
            color: "common.white",
            backgroundColor: "hsl(var(--background) / 0.18)",
            // Hit target >= 44px on every breakpoint, regardless of icon size.
            minWidth: 44,
            minHeight: 44,
            "&:hover": { backgroundColor: "hsl(var(--background) / 0.3)" },
            "&:focus-visible": {
              outline: "3px solid hsl(var(--primary))",
              outlineOffset: 2,
            },
          }}
        >
          <Close />
        </IconButton>
        {/*
          Click target: a plain <div> wrapper (not a button) so we do not
          create nested interactive controls. Clicking anywhere inside the
          paper — the safe padding, the area beside the image, or the
          image itself — closes the lightbox. Escape and the backdrop
          continue to close via Dialog's built-in `onClose`, and the X
          button is the dedicated, labelled control for keyboard / screen
          reader users.

          Layout: the wrapper takes the full paper area and centers its
          child using flex; the child (the image) has `width: 100%;
          height: 100%; max-width/max-height: 100%` plus
          `object-fit: contain`, so portrait screenshots grow to fill the
          available height, landscape screenshots grow to fill the width,
          and nothing is ever cropped. The wrapper scrolls on overflow so
          very tall screenshots remain reachable.

          The image explicitly calls `setOpen(false)` on click rather than
          relying solely on event bubbling into the wrapper — either path
          closes the dialog, and both are idempotent.
        */}
        <Box
          onClick={closeLightbox}
          role="presentation"
          sx={{
            boxSizing: "border-box",
            width: "100%",
            minHeight: "100%",
            maxWidth: "100%",
            maxHeight: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "zoom-out",
            // Small uniform padding keeps the image from touching the
            // viewport edges while still filling the modal area.
            p: { xs: 1.5, md: 3 },
          }}
        >
          {open ? (
            <Image
              src={src}
              alt={alt}
              width={width}
              height={height}
              sizes="100vw"
              loading="eager"
              onClick={closeLightbox}
              style={{
                display: "block",
                width: "100%",
                height: "100%",
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "contain",
                cursor: "zoom-out",
              }}
            />
          ) : null}
        </Box>
      </Dialog>
    </>
  );
}
